import * as React from 'react';

// API
import apibridge from '../../apibridge';

// UTILS
import { validationErrorsToString } from '../../utils';

// PACKAGES
import { Formik, Form, Field, ErrorMessage } from 'formik';
import * as Yup from 'yup';
import { SwitchTransition, CSSTransition } from 'react-transition-group';

// COMPONENTS
import Button from '../../components/Button';
import FormErrorMessage from '../../components/ErrorMessage';
import LoadingSpinner from '../../components/LoadingSpinner';
import DisplayDateExpiry from '../../components/DisplayDateExpiry';
import DisplayDate from '../../components/DisplayDate';

// ICONS
import { ReactComponent as DownloadIcon } from '../../assets/svg/download.svg';

// TYPES
import {
	DomainCertificationType,
	RequestsToJoinFetchCertification,
	RequestsToJoinReviewCertificationCertificationReview
} from '../../api/models';
import { closeModalType } from '../../components/GlobalModal';
import { RequiredValidationCertificationsStatus } from './RequestsToJoinDetail';

interface DetailModalTypes {
	modalDetail: RequestsToJoinFetchCertification;
	closeModal?: closeModalType;
	requiredValidationCertificationsStatus: RequiredValidationCertificationsStatus[] | undefined;
	setRequiredValidationCertificationsStatus: React.Dispatch<
		React.SetStateAction<RequiredValidationCertificationsStatus[] | undefined>
	>;
}

const DetailModal: React.FC<DetailModalTypes> = ({
	modalDetail,
	closeModal,
	requiredValidationCertificationsStatus,
	setRequiredValidationCertificationsStatus
}) => {
	const [showError, setShowError] = React.useState<boolean>(false);

	const [isLoading, setIsLoading] = React.useState<boolean>(false);

	const [formErrorMessage, setFormErrorMessage] = React.useState<string>();

	const {
		certificationTypeDisplayName,
		certificationType,
		state,
		certificationNumber,
		userAcknowledgement,
		expiry,
		date,
		id,
		attachments
	} = modalDetail;

	// Set up State to get all Certification Detail Data
	const initialCertificationDetail: RequestsToJoinReviewCertificationCertificationReview = {
		id: undefined,
		certificationType: undefined,
		reviewType: undefined,
		displayName: undefined,
		date: undefined,
		expiry: undefined,
		state: undefined,
		certificationNumber: undefined,
		confirmed: undefined,
		organisation: undefined,
		title: undefined,
		cited: undefined,
		attachments: []
	};

	const [certificationDetail, setCertificationDetail] =
		React.useState<RequestsToJoinReviewCertificationCertificationReview>(initialCertificationDetail);

	const getCertificationDetail = async () => {
		setIsLoading(true);

		const response = await apibridge.getCertificationDetail(id || '');

		response.result = response.result || initialCertificationDetail;

		if (response.isError) {
			setIsLoading(false);

			setFormErrorMessage(
				response.validationErrors.length
					? validationErrorsToString(response.validationErrors)
					: response.message || 'An error has occurred'
			);
		} else {
			setIsLoading(false);

			setCertificationDetail(response.result);
		}
	};

	React.useEffect(() => {
		getCertificationDetail();
	}, []);

	// Find the Id of the registration
	const registrationStatus = requiredValidationCertificationsStatus?.find((item) => item.id === modalDetail.id);

	const registrationId = registrationStatus?.id;

	// Check if the certification is confirmed
	const isCertificationsConfirmed = registrationStatus?.isRegistrationConfirmed;

	// Submit Logic
	const submitHandler = async (values: any) => {
		setIsLoading(true);

		if (certificationType === DomainCertificationType.TeacherRegistration) {
			requiredValidationCertificationsStatus?.forEach((item) => {
				if (item.id === registrationId) {
					item.isRegistrationConfirmed = values.teacherRegistrationConfirmInput;
				}
			});

			setRequiredValidationCertificationsStatus(requiredValidationCertificationsStatus);
		}

		if (certificationType === DomainCertificationType.WorkingWithChildrenCheck) {
			requiredValidationCertificationsStatus?.forEach((item) => {
				if (item.id === registrationId) {
					item.isRegistrationConfirmed = values.childrenCheckConfirmInput;
				}
			});

			setRequiredValidationCertificationsStatus(requiredValidationCertificationsStatus);
		}

		if (closeModal) {
			closeModal();
		}
	};

	const validationSchema = Yup.object({
		// isCitedInput: Yup.boolean().required('Required')
	});

	const initialValues = {
		certificationType: certificationDetail.certificationType,
		teacherRegistrationConfirmInput: isCertificationsConfirmed,
		childrenCheckConfirmInput: isCertificationsConfirmed
	};

	return (
		<Formik
			initialValues={initialValues}
			validationSchema={validationSchema}
			onSubmit={submitHandler}
			enableReinitialize
		>
			{(formik) => {
				return (
					<Form translate="yes" className="general-form mtop-0">
						<>
							{showError ? (
								<FormErrorMessage message={formErrorMessage} />
							) : (
								<SwitchTransition mode="out-in">
									<CSSTransition
										key={`key_${isLoading}`}
										timeout={600}
										classNames="standalone-fade"
										mountOnEnter
										unmountOnExit
									>
										{isLoading ? (
											<LoadingSpinner show={isLoading} />
										) : (
											<>
												<h3 className="document-title">{certificationTypeDisplayName}</h3>
												<section className="general-document-detail row">
													{certificationType && (
														<>
															<div className="col-4 document-name">Type</div>

															<div className="col-8 document-detail">{certificationTypeDisplayName}</div>
														</>
													)}

													{certificationDetail.organisation && (
														<>
															<div className="col-4 document-name">Organisation</div>

															<div className="col-8 document-detail">{certificationDetail.organisation}</div>
														</>
													)}
													{certificationDetail.title && (
														<>
															<div className="col-4 document-name">Title</div>

															<div className="col-8 document-detail">{certificationDetail.title}</div>
														</>
													)}

													{state && (
														<>
															<div className="col-4 document-name">State</div>

															<div className="col-8 document-detail">{state}</div>
														</>
													)}

													{certificationNumber && (
														<>
															<div className="col-4 document-name">Number</div>

															<div className="col-8 document-detail">{certificationNumber}</div>
														</>
													)}

													{expiry && (
														<>
															<div className="col-4 document-name">Expiry</div>

															<div className="col-8 document-detail">
																<DisplayDateExpiry>{expiry}</DisplayDateExpiry>
															</div>
														</>
													)}

													{date && (
														<>
															<div className="col-4 document-name">Date</div>

															<div className="col-8 document-detail">
																<DisplayDate>{date}</DisplayDate>
															</div>
														</>
													)}

													{userAcknowledgement &&
														certificationType !== DomainCertificationType.VaccinationCertificate && (
															<>
																<div className="col-4 document-name">Notified Authority</div>

																<div className="col-8 document-detail">
																	<Field
																		readOnly
																		type="checkbox"
																		checked={userAcknowledgement}
																		name="userAcknowledgement"
																	/>
																</div>
															</>
														)}

													{userAcknowledgement &&
														certificationType === DomainCertificationType.VaccinationCertificate && (
															<>
																<div className="col-4 document-name">User acknowledged</div>

																<div className="col-8 document-detail">
																	<Field
																		readOnly
																		type="checkbox"
																		checked={userAcknowledgement}
																		name="userAcknowledgement"
																	/>
																</div>
															</>
														)}
												</section>

												{attachments && attachments.length > 0 && (
													<div className="form-group">
														<label htmlFor="additionalCommentInput" className="label">
															Attachments
														</label>

														<ul>
															{attachments.map((item, index) => {
																return (
																	<li key={index}>
																		{item && item.attachmentUrl && (
																			<a className="attachments-download-link" href={item.attachmentUrl} download>
																				<span className="link-name">{item.displayFileName}</span>

																				<span className="link-text d-none d-lg-block">Download</span>

																				<DownloadIcon className="link-icon d-block d-lg-none" />
																			</a>
																		)}
																	</li>
																);
															})}
														</ul>
													</div>
												)}

												{/* IMPORTANT NOTE: CLIENT SPECIFICALLY ASKED NOT TO DELETE THESE "SIGHTED FUNCTIONALITY" AND
												ONLY COMMENT THEM OUT */}

												{/* <div className="form-group">
													<label className="form-control-checkbox">
														<Field type="checkbox" name="isCitedInput" />

														<span>I have physically sighted this document</span>
													</label>

													<ErrorMessage name="isCitedInput" component="div" className="form-validation-error" />
												</div> */}

												{certificationType === DomainCertificationType.TeacherRegistration && (
													<div className="form-group">
														<label className="form-control-checkbox">
															<Field type="checkbox" name="teacherRegistrationConfirmInput" />

															<span>
																I have checked with the teacher registration authority and confirmed that this person
																has a valid and current teacher registration.
															</span>
														</label>

														<ErrorMessage
															name="teacherRegistrationConfirmInput"
															component="div"
															className="form-validation-error"
														/>
													</div>
												)}

												{certificationType === DomainCertificationType.WorkingWithChildrenCheck && (
													<div className="form-group">
														<label className="form-control-checkbox">
															<Field type="checkbox" name="childrenCheckConfirmInput" />

															<span>
																I have checked with the Working With Children Check authority and confirmed that this
																person has a valid and current WWCC and is authorised to work or volunteer with
																children.
															</span>
														</label>

														<ErrorMessage
															name="childrenCheckConfirmInput"
															component="div"
															className="form-validation-error"
														/>
													</div>
												)}

												{(certificationType === DomainCertificationType.WorkingWithChildrenCheck ||
													certificationType === DomainCertificationType.TeacherRegistration) && (
													<div className="form-button-group">
														<Button type="submit" className="btn-primary w-100">
															Save Change
														</Button>
													</div>
												)}

												<Button
													className="btn-secondary w-100 mtop-16"
													onClick={() => {
														if (closeModal) {
															closeModal();
														}
													}}
												>
													Cancel
												</Button>
											</>
										)}
									</CSSTransition>
								</SwitchTransition>
							)}
						</>
					</Form>
				);
			}}
		</Formik>
	);
};

export default DetailModal;
